import myAxios from "./axiositeachWrapper";
import Config from "../_config/config";

export interface BrandingConfig {
  logo: string;
  iLearnPrimaryLogo: string;
  receiptLogo: string;
  logo192: string;
  logo512: string;
  cover: string;
  desktopHeroImage: string;
  iteachHeroImage: string;
  logoHeight: string;
  logoWidth: string;
  boxShadow: string;
  _rowExists?: boolean;
  _resourceId?: number;
}

export type BrandingSlot =
  | "logo"
  | "iLearnPrimaryLogo"
  | "receiptLogo"
  | "logo192"
  | "logo512"
  | "cover"
  | "desktopHeroImage"
  | "iteachHeroImage";

export interface AppPropertiesResult extends Record<string, any> {
  _key?: string;
  _url?: string;
  _exists?: boolean;
}

/**
 * Tenant theme (S3 app-properties.json `theme` block). Consumed at boot by
 * BOTH frontends via src/theme/themeEngine; presets ship in
 * src/theme/presets.generated.ts (source: tooling/theme/theme-presets.json).
 */
export interface ThemeConfig {
  preset: string;
  overrides?: { primary?: string; accent?: string };
  darkMode?: { enabled?: boolean; default?: "light" | "dark" | "system" };
  version?: number;
  updatedAt?: string;
}

/**
 * Tenant footer (S3 app-properties.json `footer` block). Consumed by ilearn's
 * Footer component. Null = not configured — ilearn falls back to the legacy
 * staticfiles footer.html, then to its built-in default footer.
 */
export interface FooterLink {
  label: string;
  url: string;
}

export interface FooterColumn {
  title: string;
  links: FooterLink[];
}

export type FooterSocialType = "facebook" | "x" | "instagram" | "linkedin" | "youtube" | "website";

export interface FooterSocial {
  type: FooterSocialType;
  url: string;
}

export interface FooterStructured {
  tagline?: string;
  /** `{year}` is replaced with the current year at render time. */
  copyrightText?: string;
  contactEmail?: string;
  showPoweredBy?: boolean;
  columns?: FooterColumn[];
  social?: FooterSocial[];
}

export interface FooterConfig {
  /** "default" is write-only: saving it deletes the footer key. */
  mode: "structured" | "html" | "default";
  structured?: FooterStructured;
  html?: string;
  version?: number;
  updatedAt?: string;
}

/**
 * Navigation link (S3 app-properties.json `navbar.navbarLinks`). Rendered in
 * ilearn's top navbar for logged-in students. `newTab` maps to the stored
 * `target` flag (opens in a new browser tab).
 */
export interface NavbarLink {
  name: string;
  link: string;
  icon?: string;
  newTab?: boolean;
}

export interface ScormConfig {
  appId: string;
  serviceUrl: string;
  defaultRedirectUrl: string;
  postbackUrl: string;
  /** Whether a secret is already stored (the secret itself is never returned). */
  hasSecret: boolean;
  hasPostbackSecret: boolean;
}

/** Attendance Sessions leaf columns, keyed by their CellMetrics field. */
export const ATTENDANCE_COLUMN_KEYS = [
  "p",
  "totalHeld",
  "held",
  "exam",
  "diffPH",
  "swapped",
  "cancelled",
  "additional",
  "holiday",
] as const;

export type AttendanceColumnKey = (typeof ATTENDANCE_COLUMN_KEYS)[number];

export interface LcmConfig {
  /** Show the LessonPlan Coverage column group (Planned / Covered / Difference). */
  LessonPlan_Coverage: boolean;
  /**
   * Master switch for the Attendance Sessions column group. When off the whole
   * group is hidden regardless of `attendanceColumns`.
   */
  Attendance_Sessions: boolean;
  /** Per-column switches within the group — all default-on. */
  attendanceColumns: Record<AttendanceColumnKey, boolean>;
}

export interface SystemConfigRuntime {
  /** Zone the CONVERT_TZ SQL cache is using right now (hot-reloads on save). */
  cacheTimeZone: string;
  cacheUtcOffset: string;
  cacheTzLabel: string;
  /** Zone the process + DB pool were booted with — changes only on restart. */
  bootTimeZone: string;
  bootUtcOffset: string;
  serverUtcNow: string;
}

export interface SystemConfig {
  /** Saved IANA zone from JsonConfig.system; "" = implicit IST default. */
  timeZone: string;
  ilearnPublicUrl: string;
  /** "advanced" = Email column holds per-audience JSON; not editable here. */
  emailMode: "simple" | "advanced";
  emailAddress: string;
  emailDisplayName: string;
  smsSenderId: string;
  runtime: SystemConfigRuntime;
}

export interface SystemConfigPatch {
  timeZone?: string;
  ilearnPublicUrl?: string;
  emailAddress?: string;
  emailDisplayName?: string;
  smsSenderId?: string;
}

export interface SystemConfigSaveResult {
  timeZone: string;
  ilearnPublicUrl: string;
  cacheReloaded: boolean;
  restartRequired: boolean;
}

/**
 * Atlas registration + branding settings (S3 app-properties.json `atlas`
 * block). `effective` is what the server will actually use right now —
 * tenant override first, platform default as fallback — with each id
 * validated against this tenant's own masters. `defaultProgramValid` false
 * means Atlas registration is broken for students (error code ATL-1001).
 */
export interface AtlasEffectiveConfig {
  brandName: string;
  smsTemplate: string;
  defaultProgramId: number | null;
  defaultProgramSource: "tenant" | "platform" | null;
  defaultProgramValid: boolean;
  partnerId: number | null;
  partnerSource: "tenant" | "platform" | null;
  partnerValid: boolean;
}

export interface AtlasConfigResult {
  atlas: Record<string, any> | null;
  effective: AtlasEffectiveConfig;
  programs: { id: number; name: string }[];
  schools: { id: number; name: string }[];
}

export interface AtlasConfigPatch {
  brandName?: string;
  smsTemplate?: string;
  defaultProgramId?: number | null;
  partnerId?: number | null;
}

export interface ScormConfigPatch {
  appId?: string;
  serviceUrl?: string;
  defaultRedirectUrl?: string;
  postbackUrl?: string;
  /** Only send when the admin typed a new secret; omit/empty preserves the stored one. */
  secretKey?: string;
  postbackSecret?: string;
}

class TenantPreferencesService {
  private baseUrl = `${Config.nodeSiteUrl}v1/tenant-config`;

  async getBranding(): Promise<BrandingConfig> {
    const res = await myAxios.get(`${this.baseUrl}/branding`);
    return (res.data as any).data as BrandingConfig;
  }

  async saveBranding(patch: Partial<BrandingConfig>): Promise<{ data: BrandingConfig; warning?: string }> {
    const res = await myAxios.put(`${this.baseUrl}/branding`, patch);
    const body = (res.data as any) || {};
    return { data: body.data as BrandingConfig, warning: body.warning };
  }

  async uploadLogo(
    file: File,
    slot: BrandingSlot,
    onProgress?: (pct: number) => void,
  ): Promise<{ url: string; slot: string; key: string }> {
    const fd = new FormData();
    fd.append("file", file);
    const res = await myAxios.post(
      `${this.baseUrl}/branding/upload?slot=${encodeURIComponent(slot)}`,
      fd,
      {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (ev) => {
          if (ev.total && onProgress) onProgress(Math.round((ev.loaded * 100) / ev.total));
        },
      },
    );
    return (res.data as any).data;
  }

  async getAppProperties(): Promise<AppPropertiesResult> {
    const res = await myAxios.get(`${this.baseUrl}/app-properties`);
    return (res.data as any).data as AppPropertiesResult;
  }

  async saveAppProperties(
    properties: Record<string, any>,
    mode: "replace" | "merge" = "replace",
  ): Promise<AppPropertiesResult> {
    const res = await myAxios.put(`${this.baseUrl}/app-properties`, { properties, mode });
    return (res.data as any).data as AppPropertiesResult;
  }

  // Theme (app-properties.json `theme` block). Null = not configured yet —
  // both apps fall back to their bundled defaults (indigo / classic-blue).
  async getThemeConfig(): Promise<ThemeConfig | null> {
    const res = await myAxios.get(`${this.baseUrl}/theme`);
    return (res.data as any).data as ThemeConfig | null;
  }

  async saveThemeConfig(theme: ThemeConfig): Promise<ThemeConfig> {
    const res = await myAxios.put(`${this.baseUrl}/theme`, theme);
    return (res.data as any).data as ThemeConfig;
  }

  // Footer (app-properties.json `footer` block). Null = not configured —
  // ilearn falls back to legacy footer.html, then its built-in default.
  async getFooterConfig(): Promise<FooterConfig | null> {
    const res = await myAxios.get(`${this.baseUrl}/footer`);
    return (res.data as any).data as FooterConfig | null;
  }

  /** Saving { mode: "default" } deletes the key; the server returns null. */
  async saveFooterConfig(footer: FooterConfig): Promise<FooterConfig | null> {
    const res = await myAxios.put(`${this.baseUrl}/footer`, footer);
    return (res.data as any).data as FooterConfig | null;
  }

  // Navigation (app-properties.json `navbar.navbarLinks`). The top-navbar links
  // logged-in students see in ilearn (e.g. "Courses" → /Products).
  async getNavbarLinks(): Promise<NavbarLink[]> {
    const res = await myAxios.get(`${this.baseUrl}/navbar`);
    return ((res.data as any).data?.links ?? []) as NavbarLink[];
  }

  async saveNavbarLinks(links: NavbarLink[]): Promise<NavbarLink[]> {
    const res = await myAxios.put(`${this.baseUrl}/navbar`, { links });
    return ((res.data as any).data?.links ?? []) as NavbarLink[];
  }

  // iLearn feature flags (customer_detail_master.iLearnAppConfig.features)
  // plus Student Hub hero-button flags (iLearnAppConfig.studentHub.heroButtons).
  // Default-on: a key hides its feature/button only when explicitly false.
  async getIlearnFeatures(): Promise<{
    features: Record<string, boolean>;
    heroButtons: Record<string, boolean>;
  }> {
    const res = await myAxios.get(`${this.baseUrl}/ilearn-features`);
    const body = (res.data as any) || {};
    return { features: body.features || {}, heroButtons: body.heroButtons || {} };
  }

  async saveIlearnFeatures(patch: {
    features?: Record<string, boolean>;
    heroButtons?: Record<string, boolean>;
  }): Promise<{ features: Record<string, boolean>; heroButtons: Record<string, boolean> }> {
    const res = await myAxios.put(`${this.baseUrl}/ilearn-features`, patch);
    const body = (res.data as any) || {};
    return { features: body.features || {}, heroButtons: body.heroButtons || {} };
  }

  // Atlas registration + branding (app-properties.json `atlas` block).
  // The save endpoint validates ids against the tenant's own masters, so a
  // broken program/partner can never be stored from here.
  async getAtlasConfig(): Promise<AtlasConfigResult> {
    const res = await myAxios.get(`${this.baseUrl}/atlas`);
    return (res.data as any).data as AtlasConfigResult;
  }

  async saveAtlasConfig(patch: AtlasConfigPatch): Promise<Record<string, any>> {
    const res = await myAxios.put(`${this.baseUrl}/atlas`, patch);
    return (res.data as any).data as Record<string, any>;
  }

  async getScormConfig(): Promise<ScormConfig> {
    const res = await myAxios.get(`${this.baseUrl}/scorm`);
    return (res.data as any).data as ScormConfig;
  }

  async saveScormConfig(patch: ScormConfigPatch): Promise<ScormConfig> {
    const res = await myAxios.put(`${this.baseUrl}/scorm`, patch);
    return (res.data as any).data as ScormConfig;
  }

  // Lesson Plan Coverage Report section toggles
  // (customer_detail_master.iTeachAppConfig.lessonPlanCoverageReport)
  async getLcmConfig(): Promise<LcmConfig> {
    const res = await myAxios.get(`${this.baseUrl}/lcm`);
    return (res.data as any).data as LcmConfig;
  }

  async saveLcmConfig(patch: Partial<LcmConfig>): Promise<LcmConfig> {
    const res = await myAxios.put(`${this.baseUrl}/lcm`, patch);
    return (res.data as any).data as LcmConfig;
  }

  // System settings: tenant timezone (customer_detail_master.JsonConfig.system),
  // iLearn public URL, default email/SMS sender columns
  async getSystemConfig(): Promise<SystemConfig> {
    const res = await myAxios.get(`${this.baseUrl}/system`);
    return (res.data as any).data as SystemConfig;
  }

  async saveSystemConfig(patch: SystemConfigPatch): Promise<SystemConfigSaveResult> {
    const res = await myAxios.put(`${this.baseUrl}/system`, patch);
    return (res.data as any).data as SystemConfigSaveResult;
  }

  // Invoice generation: which document template renders invoices
  // (resource_master(Name='InvoiceRunDetails').JsonConfig.postboxTemplateIdForInvoiceGeneration).
  async getInvoiceConfig(): Promise<{ activeTemplateId: number | null }> {
    const res = await myAxios.get(`${this.baseUrl}/invoice`);
    return (res.data as any).data as { activeTemplateId: number | null };
  }

  async saveInvoiceConfig(activeTemplateId: number | null): Promise<{ activeTemplateId: number | null }> {
    const res = await myAxios.put(`${this.baseUrl}/invoice`, { activeTemplateId });
    return (res.data as any).data as { activeTemplateId: number | null };
  }
}

export default new TenantPreferencesService();
